import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classes from './MainContainer.module.css';
import { Link } from 'react-router-dom';

import { Studio } from './Studio';

import useFetchUserReviews from '../../hooks/reviews/useFetchUserReviews';

import Reviews from '../../pages/StudioProfile/Reviews';

export const MainContainer = ({ user, studios= [], isLoading }) => {
    const { user: authUser } = useSelector((state) => state?.auth || {});
    const isOwnProfile = Boolean(authUser?.id && user?.id && authUser.id === user.id);
    const [tabletMode, setTabletMode] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [text, setText] = useState('');
    const fullText = user?.description || '';

    const { reviews: processedReviews, loading: reviewsLoading } = useFetchUserReviews({
        userId: user?.id,
        limit: 10,
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        const handleResize = () => {
          setTabletMode(window.innerWidth > 742);
        };
    
        handleResize();
    
        window.addEventListener('resize', handleResize);
    
        return () => window.removeEventListener('resize', handleResize);
      }, []);
    
    const totalSlides = Math.ceil(studios.length / 2);

    const handlePrevClick = () => {
        setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex - 1;
            return newIndex < 0 ? totalSlides - 1 : newIndex;
        });
    };
    
    const handleNextClick = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
    };

    const getTranslateX = () => {
        const slidePercentage = 100 / totalSlides;
        return -(currentIndex * slidePercentage);
    };

    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.touches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.touches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNextClick();
        } else if (isRightSwipe) {
            handlePrevClick();
        }
    };
    useEffect(() => {
        if (fullText) {
            setText(isExpanded ? fullText : `${fullText.slice(0, 270)}...`);
        }
    }, [isExpanded, fullText]);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    if (isLoading) {
        return (
          <div className={classes.mainContainerSkeleton}>
            <div className={classes.headerSkeleton} />
            <div className={classes.studiosSkeleton}>
              {[1, 2, 3].map((item) => (
                <div key={item} className={classes.studioCardSkeleton}>
                  <div className={classes.imagesSkeleton} />
                  <div className={classes.contentSkeleton}>
                    <div className={classes.titleSkeleton} />
                    <div className={classes.descriptionSkeleton} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.userNameContainer}>
              <h1 className={`${classes.userName} titleSemibold2`}>{user?.firstName || ''}</h1>
              {isOwnProfile && (
                <Link
                  to="/profile"
                  className={`${classes.editButton} mediumLarge`}
                >
                  Bearbeiten
                </Link>
              )}
            </div>
            <div className={classes.line}></div>
            {fullText &&
            <div className={classes.userDescription}>
                <h3 className={`${tabletMode ? 'titleSemibold3' : 'semiboldLarge'}`}>Über mich</h3>
                <p 
                    className={`regularLarge ${classes.expandableText}`}
                    onClick={toggleExpand}
                >
                    {text}
                </p>
                <span className={`${classes.readMore} semiboldLarge`} onClick={toggleExpand}>
                    {!isExpanded && ('Weiterlesen')}
                    {isExpanded && ('Weniger anzeigen')}
                </span>
            </div>
            }
            {fullText &&
                <div className={classes.line}></div>
            }
            <div className={classes.studioGallery}>
                <div className={classes.studioGalleryHeader}>
                    {studios.length > 0 && (
                    <h3 className={`${tabletMode ? 'titleSemibold3' : 'semiboldLarge'}`}>Studios</h3>
                    )}
                    {tabletMode && studios.length > 2 && (
                        <div className={classes.arrowsContainer}>
                            <div className={classes.arrowContainer} onClick={handlePrevClick}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M0.480469 6.98971L7.22938 0.269531L8.43146 1.4665L1.68255 8.18668L0.480469 6.98971Z" fill="#1F1F1F"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.23025 14.2695L0.480469 6.98971L1.7305 5.84117L8.47941 13.1214L7.23025 14.2695Z" fill="#1F1F1F"/>
                                </svg>
                            </div>
                            <div className={classes.smallLine}></div>
                            <div className={classes.arrowContainer} onClick={handleNextClick}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M8.47551 7.54935L1.7266 14.2695L0.524514 13.0726L7.27342 6.35239L8.47551 7.54935Z" fill="#1F1F1F"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M1.72573 0.269531L8.47551 7.54935L7.22547 8.69789L0.476562 1.4177L1.72573 0.269531Z" fill="#1F1F1F"/>
                                </svg>  
                            </div>
                        </div>
                    )}
                </div>
                <div
                    className={classes.studioGalleryContainer}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                {tabletMode && (
                    <>
                    <div 
                        className={classes.studioInnerGalleryContainer} 
                        style={{ 
                            transform: `translateX(${getTranslateX()}%)`,
                            width: `${totalSlides * 100}%`
                        }}
                    >
                        {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                            <div 
                                key={slideIndex} 
                                className={classes.slidePair}
                                style={{ width: `${100 / totalSlides}%` }} // Each slide pair width is relative to total slides
                            >
                                <Studio fromUser={true} studio={studios[slideIndex * 2]} />
                                {studios[slideIndex * 2 + 1] && (
                                    <Studio fromUser={true} studio={studios[slideIndex * 2 + 1]} />
                                )}
                            </div>
                        ))}
                    </div>
                    {/* <div className={classes.allStudiosShowButton}>
                        {studios.length > 2 && (
                            <button className={`${classes.showAllStudiosButton} semiboldMiddle`}>
                                Alle {studios.length} Studios anschauen
                            </button>
                        )}
                    </div> */}
                    </>
                )}
                {!tabletMode && (
                    <div className={classes.studioGalleryMobile}>
                        {studios.map((studio, index) => (
                            <Studio key={index} studio={studio} />
                        ))}
                    </div>
                )}
                </div>
                {/* {!tabletMode && (
                    <div className={classes.allStudiosShowButton}>
                        {studios.length > 2 && (
                            <button className={`${classes.showAllStudiosButton} semiboldMiddle`}>
                                Alle {studios.length} Studios anschauen
                            </button>
                        )}
                    </div>
                )} */}
            </div>
            {studios.length > 2 && user?.reviews && (
                <div style={{marginBottom: '30.5px'}} className={classes.line}></div>
            )}
            {processedReviews?.length > 0 &&
                <Reviews reviews={processedReviews} />
            }
        </div>
  );
};

export default MainContainer;
